import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCalendarAlt, FaChartBar, FaSpinner, FaFileAlt } from 'react-icons/fa';
import { apiCall, ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { Contact } from '../../types/contact';

interface ReferralStatistics {
    total_referrals: number;
    valid_referrals: number;
    recent_referrals: number;
    referral_rate: number | string;
}

interface Referral {
    id: string;
    patient_name?: string;
    date_of_referral: string;
    reason_for_referral?: string;
    duration_months: number;
    status: 'valid' | 'expired' | string;
    receiving_provider_name?: string;
}

const ReferrerDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();

    const [referrer, setReferrer] = useState<Contact | null>(null);
    const [statistics, setStatistics] = useState<ReferralStatistics | null>(null);
    const [recentReferrals, setRecentReferrals] = useState<Referral[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadReferrerData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadReferrerData = async () => {
        if (!id) return;
        setIsLoading(true);
        try {
            // Load referrer details
            const referrerResponse = await apiCall(`${ENDPOINTS.CORE.CONTACTS}${id}/`, { method: 'GET' }, token || undefined);
            if (referrerResponse.ok) {
                const referrerData: Contact = await referrerResponse.json();
                setReferrer(referrerData);

                // Load referral statistics if this is a referrer
                if (referrerData.is_referrer) {
                    const statsResponse = await apiCall(`${ENDPOINTS.CORE.CONTACTS}${id}/referral_statistics/`, { method: 'GET' }, token || undefined);
                    if (statsResponse.ok) {
                        const statsData = await statsResponse.json();
                        setStatistics(statsData);
                    }

                    // Load recent referrals
                    const referralsResponse = await apiCall(
                        `/patients/referrals/?referrer_name=${encodeURIComponent(referrerData.full_name || '')}&limit=10`,
                        { method: 'GET' },
                        token || undefined
                    );
                    if (referralsResponse.ok) {
                        const referralsData = await referralsResponse.json();
                        setRecentReferrals(referralsData.results || referralsData);
                    }
                }
                setError(null);
            } else {
                throw new Error('Failed to load referrer details');
            }
        } catch (err) {
            console.error('Error loading referrer data:', err);
            setError('Failed to load referrer information');
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex justify-center items-center h-64">
                    <FaSpinner className="animate-spin text-4xl" />
                    <span className="ml-4 text-lg">Loading referrer details...</span>
                </div>
            </div>
        );
    }

    if (error || !referrer) {
        return (
            <div className="p-6">
                <div className="alert alert-error">
                    <span>{error || 'Referrer not found'}</span>
                </div>
                <button
                    className="btn btn-ghost mt-4"
                    onClick={() => navigate('/addressbook')}
                >
                    <FaArrowLeft className="mr-2" />
                    Back to Address Book
                </button>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center mb-6">
                <button
                    className="btn btn-ghost mr-4"
                    onClick={() => navigate('/addressbook')}
                >
                    <FaArrowLeft className="mr-2" />
                    Back to Address Book
                </button>
                <h1 className="text-2xl font-bold">Referrer Details</h1>
            </div>

            {/* Referrer Information Card */}
            <div className="card bg-base-100 shadow-xl mb-6">
                <div className="card-body">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <h2 className="card-title text-2xl mb-2">
                                {referrer.full_name}
                                {!referrer.is_referrer && (
                                    <span className="badge badge-warning ml-2">Not a Referrer</span>
                                )}
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <h3 className="font-semibold text-lg mb-2">Professional Information</h3>
                                    <div className="space-y-2">
                                        {referrer.specialty && (
                                            <p><strong>Specialty:</strong> {referrer.specialty}</p>
                                        )}
                                        {referrer.qualifications && (
                                            <p><strong>Qualifications:</strong> {referrer.qualifications}</p>
                                        )}
                                        {referrer.provider_number && (
                                            <p><strong>Provider Number:</strong> {referrer.provider_number}</p>
                                        )}
                                        {referrer.ahpra_number && (
                                            <p><strong>AHPRA Number:</strong> {referrer.ahpra_number}</p>
                                        )}
                                        {referrer.company && (
                                            <p><strong>Practice:</strong> {referrer.company}</p>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <h3 className="font-semibold text-lg mb-2">Contact Information</h3>
                                    <div className="space-y-2">
                                        {referrer.phone && (
                                            <p className="flex items-center">
                                                <FaPhone className="mr-2 text-primary" />
                                                {referrer.phone}
                                            </p>
                                        )}
                                        {referrer.mobile && (
                                            <p className="flex items-center">
                                                <FaPhone className="mr-2 text-primary" />
                                                {referrer.mobile} (Mobile)
                                            </p>
                                        )}
                                        {referrer.email && (
                                            <p className="flex items-center">
                                                <FaEnvelope className="mr-2 text-primary" />
                                                <a href={`mailto:${referrer.email}`} className="link">
                                                    {referrer.email}
                                                </a>
                                            </p>
                                        )}
                                        {referrer.full_address && (
                                            <p className="flex items-start">
                                                <FaMapMarkerAlt className="mr-2 mt-1 text-primary" />
                                                {referrer.full_address}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Communication Methods */}
                            {(referrer.argus || referrer.medical_objects || referrer.health_link || referrer.erx) && (
                                <div className="mt-4">
                                    <h3 className="font-semibold text-lg mb-2">Communication Methods</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {referrer.argus && (
                                            <div className="badge badge-outline">Argus: {referrer.argus}</div>
                                        )}
                                        {referrer.medical_objects && (
                                            <div className="badge badge-outline">Medical Objects: {referrer.medical_objects}</div>
                                        )}
                                        {referrer.health_link && (
                                            <div className="badge badge-outline">Health Link: {referrer.health_link}</div>
                                        )}
                                        {referrer.erx && (
                                            <div className="badge badge-outline">eRx: {referrer.erx}</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {referrer.notes && (
                                <div className="mt-4">
                                    <h3 className="font-semibold text-lg mb-2">Notes</h3>
                                    <p className="text-base-content/80">{referrer.notes}</p>
                                </div>
                            )}
                        </div>

                        <div className="text-right">
                            <div className={`badge ${referrer.is_active ? 'badge-success' : 'badge-error'} badge-lg`}>
                                {referrer.is_active ? 'Active' : 'Inactive'}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Referral Statistics (only show for referrers) */}
            {referrer.is_referrer && statistics && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div className="stat bg-base-100 shadow-lg rounded-lg">
                        <div className="stat-figure text-primary">
                            <FaFileAlt className="text-3xl" />
                        </div>
                        <div className="stat-title">Total Referrals</div>
                        <div className="stat-value text-primary">{statistics.total_referrals}</div>
                        <div className="stat-desc">All time</div>
                    </div>

                    <div className="stat bg-base-100 shadow-lg rounded-lg">
                        <div className="stat-figure text-success">
                            <FaChartBar className="text-3xl" />
                        </div>
                        <div className="stat-title">Valid Referrals</div>
                        <div className="stat-value text-success">{statistics.valid_referrals}</div>
                        <div className="stat-desc">Currently active</div>
                    </div>

                    <div className="stat bg-base-100 shadow-lg rounded-lg">
                        <div className="stat-figure text-warning">
                            <FaCalendarAlt className="text-3xl" />
                        </div>
                        <div className="stat-title">Recent Referrals</div>
                        <div className="stat-value text-warning">{statistics.recent_referrals}</div>
                        <div className="stat-desc">Last 6 months</div>
                    </div>

                    <div className="stat bg-base-100 shadow-lg rounded-lg">
                        <div className="stat-figure text-info">
                            <FaChartBar className="text-3xl" />
                        </div>
                        <div className="stat-title">Referral Rate</div>
                        <div className="stat-value text-info text-lg">{statistics.referral_rate}</div>
                        <div className="stat-desc">Average monthly</div>
                    </div>
                </div>
            )}

            {/* Recent Referrals Table */}
            {referrer.is_referrer && recentReferrals.length > 0 && (
                <div className="card bg-base-100 shadow-xl">
                    <div className="card-body">
                        <h3 className="card-title mb-4">Recent Referrals</h3>
                        <div className="overflow-x-auto">
                            <table className="table table-zebra">
                                <thead>
                                    <tr>
                                        <th>Patient</th>
                                        <th>Date of Referral</th>
                                        <th>Reason</th>
                                        <th>Duration</th>
                                        <th>Status</th>
                                        <th>Receiving Provider</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentReferrals.map((referral) => (
                                        <tr key={referral.id}>
                                            <td>
                                                <div className="font-semibold">
                                                    {referral.patient_name || 'Patient Name'}
                                                </div>
                                            </td>
                                            <td>
                                                {new Date(referral.date_of_referral).toLocaleDateString()}
                                            </td>
                                            <td>
                                                <div className="max-w-xs truncate" title={referral.reason_for_referral}>
                                                    {referral.reason_for_referral || 'No reason specified'}
                                                </div>
                                            </td>
                                            <td>
                                                {referral.duration_months} month{referral.duration_months !== 1 ? 's' : ''}
                                            </td>
                                            <td>
                                                <span className={`badge ${
                                                    referral.status === 'valid' ? 'badge-success' :
                                                    referral.status === 'expired' ? 'badge-warning' :
                                                    'badge-error'
                                                }`}>
                                                    {referral.status}
                                                </span>
                                            </td>
                                            <td>
                                                {referral.receiving_provider_name || 'Not assigned'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* No Referral Data Message */}
            {referrer.is_referrer && recentReferrals.length === 0 && (
                <div className="card bg-base-100 shadow-xl">
                    <div className="card-body text-center">
                        <h3 className="card-title justify-center mb-4">No Referrals Found</h3>
                        <p className="text-base-content/60">
                            This referrer has not made any referrals yet, or the referrals are not linked to their contact record.
                        </p>
                    </div>
                </div>
            )}

            {/* Non-Referrer Message */}
            {!referrer.is_referrer && (
                <div className="card bg-base-100 shadow-xl">
                    <div className="card-body text-center">
                        <h3 className="card-title justify-center mb-4">Not a Referrer</h3>
                        <p className="text-base-content/60">
                            This contact is not classified as a referrer, so no referral statistics are available.
                        </p>
                        <div className="card-actions justify-center mt-4">
                            <button
                                className="btn btn-primary"
                                onClick={() => {/* Open edit modal or navigate to edit */}}
                            >
                                Edit Contact Type
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReferrerDetails;
